import { useCallback, useEffect, useMemo, useState } from "react";
import { api, clearAuth, getAuthToken, setAuthToken } from "@/lib/api";

const IDLE_LOGOUT_MINUTES = 30;
const IDLE_MS = IDLE_LOGOUT_MINUTES * 60 * 1000;

export function useSession() {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("kc_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [permissions, setPermissions] = useState(() => {
    const raw = localStorage.getItem("kc_permissions");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  const token = getAuthToken();

  const refreshMe = useCallback(async () => {
    const t = getAuthToken();
    if (!t) {
      setUser(null);
      setPermissions(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      localStorage.setItem("kc_user", JSON.stringify(res.data));
      
      // Also refresh permissions from stored or fetch if needed
      const storedPerms = localStorage.getItem("kc_permissions");
      if (storedPerms) {
        setPermissions(JSON.parse(storedPerms));
      }
    } catch {
      clearAuth();
      setUser(null);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // inactivity logout
  useEffect(() => {
    if (!token) return undefined;

    const mark = () => {
      localStorage.setItem("kc_last_activity", String(Date.now()));
    };

    const check = () => {
      const last = Number(localStorage.getItem("kc_last_activity") || "0");
      if (last && Date.now() - last > IDLE_MS) {
        clearAuth();
        setUser(null);
        setPermissions(null);
      }
    };

    mark();
    const events = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, mark));

    const interval = window.setInterval(check, 30 * 1000); // 30s instead of 10s

    return () => {
      events.forEach((e) => window.removeEventListener(e, mark));
      window.clearInterval(interval);
    };
  }, [token]);

  const login = useCallback(async ({ email, password }) => {
    const res = await api.post("/auth/login", { email, password });
    setAuthToken(res.data.token);
    localStorage.setItem("kc_user", JSON.stringify(res.data.user));
    localStorage.setItem("kc_last_activity", String(Date.now()));
    
    // Store permissions
    if (res.data.permissions) {
      localStorage.setItem("kc_permissions", JSON.stringify(res.data.permissions));
      setPermissions(res.data.permissions);
    }
    
    setUser(res.data.user);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    localStorage.removeItem("kc_permissions");
    setUser(null);
    setPermissions(null);
  }, []);

  const role = useMemo(() => user?.role || null, [user]);

  // Helper to check if user has specific permission
  const hasPermission = useCallback((module, action) => {
    if (!permissions) return false;
    const modulePerms = permissions[module] || [];
    return modulePerms.includes(action);
  }, [permissions]);

  // Helper to check if user can view a module
  const canView = useCallback((module) => {
    return hasPermission(module, "view");
  }, [hasPermission]);

  return { user, role, permissions, loading, login, logout, refreshMe, hasPermission, canView };
}
